/**
 * CheckInHeader Component
 * Sticky header with Close button, title, and Save Draft button
 */

import { useAppTheme } from '@/providers/theme-provider';
import Ionicons from '@expo/vector-icons/Ionicons';
import React from 'react';
import { ActivityIndicator, Pressable, Text, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

export interface CheckInHeaderProps {
  /** Title to display in the center */
  title?: string;
  /** Callback when close button is pressed */
  onClose: () => void;
  /** Callback when save draft button is pressed */
  onSaveDraft?: () => void;
  /** Whether save draft is in progress */
  isSavingDraft?: boolean;
  /** Whether to show the save draft button */
  showSaveDraft?: boolean;
  /** Whether there are unsaved changes */
  hasUnsavedChanges?: boolean;
}

export function CheckInHeader({
  title = 'Check-in',
  onClose,
  onSaveDraft,
  isSavingDraft = false,
  showSaveDraft = true,
  hasUnsavedChanges = false,
}: CheckInHeaderProps) {
  const theme = useAppTheme();
  const insets = useSafeAreaInsets();

  return (
    <View
      className="flex-row items-center justify-between px-4 pb-3 border-b bg-background border-border"
      style={{ paddingTop: insets.top + 8 }}
    >
      {/* Close Button */}
      <Pressable
        onPress={onClose}
        className="p-2 -ml-2 rounded-full"
        hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
        accessibilityLabel="Close check-in"
        accessibilityRole="button"
      >
        <Ionicons name="close" size={24} className="text-foreground" />
      </Pressable>

      {/* Title */}
      <Text className="text-lg font-semibold text-foreground">
        {title}
      </Text>

      {/* Save Draft Button */}
      {showSaveDraft && onSaveDraft ? (
        <Pressable
          onPress={onSaveDraft}
          disabled={isSavingDraft || !hasUnsavedChanges}
          className="p-2 -mr-2 rounded-full"
          hitSlop={{ top: 10, right: 10, bottom: 10, left: 10 }}
          accessibilityLabel="Save draft"
          accessibilityRole="button"
          style={{ opacity: hasUnsavedChanges ? 1 : 0.5 }}
        >
          {isSavingDraft ? (
            <ActivityIndicator size="small" color={theme.colors.primary} />
          ) : (
            <Text
              className={`text-base font-medium ${hasUnsavedChanges ? 'text-primary' : 'text-muted-foreground'}`}
            >
              Save
            </Text>
          )}
        </Pressable>
      ) : (
        // Spacer to keep title centered
        <View style={{ width: 44 }} />
      )}
    </View>
  );
}

export default CheckInHeader;
